import type { HealthStatus, HealthSummary, OverallStatus } from '../api/monitor';

interface Tone {
  label: string;
  /** Classe do pontinho colorido. */
  dot: string;
  /** Classe da pílula (fundo + texto). */
  pill: string;
}

export const HEALTH_TONE: Record<HealthStatus, Tone> = {
  up: { label: 'Operacional', dot: 'bg-ok-500', pill: 'bg-ok-100 text-ok-600' },
  down: { label: 'Indisponível', dot: 'bg-danger-500', pill: 'bg-danger-100 text-danger-600' },
  unknown: {
    label: 'Não verificada',
    dot: 'bg-ink-400',
    pill: 'bg-surface-200 text-ink-600',
  },
};

interface OverallTone {
  dot: string;
  /** Cor do ícone no cabeçalho. */
  icon: string;
  /** Faixa de destaque no topo da tela de APIs. */
  banner: string;
}

/**
 * Verde só quando todas as APIs responderam 2XX; amarelo quando parte delas
 * está fora (ou ainda não foi checada); vermelho quando nenhuma responde.
 */
export const OVERALL_TONE: Record<OverallStatus, OverallTone> = {
  ok: {
    dot: 'bg-ok-500',
    icon: 'text-ok-600',
    banner: 'bg-ok-100 border-ok-200 text-ok-600',
  },
  partial: {
    dot: 'bg-warn-500',
    icon: 'text-warn-700',
    banner: 'bg-warn-100 border-warn-200 text-warn-700',
  },
  down: {
    dot: 'bg-danger-500',
    icon: 'text-danger-600',
    banner: 'bg-danger-100 border-danger-200 text-danger-600',
  },
  unknown: {
    dot: 'bg-ink-400',
    icon: 'text-ink-400',
    banner: 'bg-surface-200 border-line-200 text-ink-600',
  },
};

export function overallLabel(summary: HealthSummary): string {
  const { status, total, down, unknown } = summary;

  if (total === 0) return 'Nenhuma API cadastrada';
  if (status === 'unknown') return 'Aguardando a primeira verificação';
  if (status === 'ok') return `Todas as ${total} APIs operacionais`;
  if (status === 'down') return `Todas as ${total} APIs estão indisponíveis`;

  const parts: string[] = [];
  if (down > 0) parts.push(`${down} de ${total} ${down === 1 ? 'API' : 'APIs'} indisponível`);
  if (unknown > 0) parts.push(`${unknown} ainda sem verificação`);

  return parts.join(' · ');
}

export function formatLatency(latencyMs: number | null): string {
  if (latencyMs === null) return '—';
  if (latencyMs < 1000) return `${latencyMs} ms`;
  return `${(latencyMs / 1000).toFixed(1)} s`;
}

/** "há 38 s", "há 4 min", "ontem 14:32" — o suficiente para julgar se está fresco. */
export function formatCheckedAt(checkedAt: string | null): string {
  if (!checkedAt) return 'nunca verificada';

  const checked = new Date(checkedAt);
  const seconds = Math.round((Date.now() - checked.getTime()) / 1000);

  if (seconds < 0) return 'agora';
  if (seconds < 60) return `há ${seconds} s`;
  if (seconds < 3600) return `há ${Math.floor(seconds / 60)} min`;
  if (seconds < 86400) return `há ${Math.floor(seconds / 3600)} h`;

  return checked.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Motivo curto da falha, já formatado para a linha vermelha da lista. */
export function failureMessage(api: {
  health: { status: HealthStatus; httpStatus: number | null; error: string | null };
}): string | null {
  if (api.health.status !== 'down') return null;
  return api.health.error || 'A API não respondeu à verificação.';
}
